import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Search, RefreshCw, AlertTriangle, CheckCircle, Clock, MapPin, Package, User, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { exportArrayToCsv } from '@/lib/csvExport';

const SFGInvestigationQueue = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [reportedByFilter, setReportedByFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('bundle') || '');
  
  // Resolve dialog state
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [resolution, setResolution] = useState('');
  const [actionTaken, setActionTaken] = useState('');
  const [resolving, setResolving] = useState(false);
  
  // Mark Missing dialog state
  const [showMarkMissingDialog, setShowMarkMissingDialog] = useState(false);
  const [missingBundleCode, setMissingBundleCode] = useState('');
  const [missingReason, setMissingReason] = useState('not_found');
  const [missingNotes, setMissingNotes] = useState('');
  const [markingMissing, setMarkingMissing] = useState(false);

  const fetchItems = async (overrideQuery) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('status', statusFilter);
      params.append('limit', '1000');
      const q = (overrideQuery !== undefined ? overrideQuery : searchQuery).trim();
      if (q) params.append('bundle_code', q);
      const response = await apiClient.get(`/wms/sfg/investigation-queue?${params.toString()}`);
      setItems(response.data.items || []);
    } catch (error) {
      toast.error('Failed to fetch investigation queue');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Debounced server-side search by bundle_code so we hit the full DB even
  // when the queue has hundreds of pending items.
  useEffect(() => {
    const t = setTimeout(() => {
      fetchItems(searchQuery);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const exportCsv = () => {
    if (!items.length) {
      toast.error('Nothing to export — the queue is empty for this filter.');
      return;
    }
    const stamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19);
    exportArrayToCsv(
      items,
      [
        { key: 'investigation_id', header: 'Investigation ID' },
        { key: 'type', header: 'Type' },
        { key: 'bundle_code', header: 'Bundle Code' },
        { key: 'slip_no', header: 'Pick Slip' },
        { key: 'bin_location', header: 'Bin' },
        { key: 'sku_code', header: 'SKU' },
        { key: 'design_name', header: 'Design' },
        { key: 'usn_count', header: 'USN Count' },
        { key: 'reason', header: 'Reason' },
        { key: 'notes', header: 'Notes' },
        { key: 'priority', header: 'Priority' },
        { key: 'reported_by', header: 'Reported By' },
        { key: 'reported_by_name', header: 'Reported By Name' },
        { key: 'reported_at', header: 'Reported At' },
        { key: 'status', header: 'Status' },
        { key: 'resolution', header: 'Resolution' },
        { key: 'action_taken', header: 'Action Taken' },
        { key: 'resolved_by', header: 'Resolved By' },
        { key: 'resolved_by_name', header: 'Resolved By Name' },
        { key: 'resolved_at', header: 'Resolved At' },
      ],
      `sfg_investigation_queue_${statusFilter}_${stamp}.csv`,
    );
    toast.success(`Exported ${items.length} ticket${items.length === 1 ? '' : 's'} to CSV.`);
  };

  const handleResolve = async () => {
    if (!resolution) {
      toast.error('Please select a resolution');
      return;
    }

    setResolving(true);
    try {
      await apiClient.post(`/wms/sfg/investigation-queue/${selectedItem.investigation_id}/resolve`, null, {
        params: {
          resolution,
          action_taken: actionTaken
        }
      });
      toast.success(`Investigation resolved: ${resolution === 'found' ? 'Bundle restored to STORED' : resolution === 'written_off' ? 'Bundle written off' : 'Resolved'}`);
      setShowResolveDialog(false);
      setSelectedItem(null);
      setResolution('');
      setActionTaken('');
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resolve investigation');
    } finally {
      setResolving(false);
    }
  };

  const handleMarkMissing = async () => {
    if (!missingBundleCode.trim()) {
      toast.error('Please enter a bundle code');
      return;
    }
    setMarkingMissing(true);
    try {
      const response = await apiClient.post('/wms/sfg/inventory-adjustment/mark-missing', null, {
        params: {
          bundle_code: missingBundleCode.trim(),
          reason: missingReason,
          notes: missingNotes
        }
      });
      toast.success(response.data.message);
      setShowMarkMissingDialog(false);
      setMissingBundleCode('');
      setMissingReason('not_found');
      setMissingNotes('');
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark bundle as missing');
    } finally {
      setMarkingMissing(false);
    }
  };

  const getReasonBadge = (reason) => {
    const colors = {
      not_found: 'bg-red-100 text-red-800 border-red-200',
      damaged: 'bg-orange-100 text-orange-800 border-orange-200',
      misplaced: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      other: 'bg-gray-100 text-gray-800 border-gray-200'
    };
    const labels = {
      not_found: 'Not Found',
      damaged: 'Damaged',
      misplaced: 'Misplaced',
      other: 'Other'
    };
    return (
      <Badge className={`${colors[reason] || colors.other} border`}>
        {labels[reason] || reason}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    if (priority === 'high') {
      return <Badge className="bg-red-600 text-white">High Priority</Badge>;
    }
    return <Badge variant="outline">Medium</Badge>;
  };

  const getStatusBadge = (status) => {
    if (status === 'pending') {
      return <Badge className="bg-amber-100 text-amber-800 border border-amber-200">Pending</Badge>;
    }
    return <Badge className="bg-green-100 text-green-800 border border-green-200">Resolved</Badge>;
  };

  const filteredItems = items.filter(item => {
    // Reported By filter
    if (reportedByFilter !== 'all') {
      const name = item.reported_by_name || item.reported_by || '';
      if (name !== reportedByFilter) return false;
    }
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.bundle_code?.toLowerCase().includes(query) ||
      item.slip_no?.toLowerCase().includes(query) ||
      item.design_name?.toLowerCase().includes(query) ||
      item.reported_by_name?.toLowerCase().includes(query)
    );
  });

  // Unique reporter list for the dropdown — derived from currently-loaded items
  // (so it always matches what the user could see for the active status).
  const reporterOptions = React.useMemo(() => {
    const names = new Set();
    items.forEach((it) => {
      const n = it.reported_by_name || it.reported_by;
      if (n && String(n).trim()) names.add(String(n).trim());
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">SFG Investigation Queue</h1>
              <p className="text-sm text-gray-500">Review bundles reported as not found, damaged, or misplaced</p>
            </div>
            <Button 
              onClick={() => setShowMarkMissingDialog(true)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="mark-missing-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Mark Bundle Missing
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search bundle, slip, design..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                    data-testid="search-input"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
              <Select value={reportedByFilter} onValueChange={setReportedByFilter}>
                <SelectTrigger className="w-[200px]" data-testid="reported-by-filter">
                  <SelectValue placeholder="Reported By" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All reporters</SelectItem>
                  {reporterOptions.map((name) => (
                    <SelectItem key={name} value={name} data-testid={`reported-by-option-${name}`}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchItems} disabled={loading} data-testid="refresh-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="outline"
                onClick={exportCsv}
                disabled={!items.length}
                data-testid="sfg-inv-export-csv"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                title="Download the currently visible queue as CSV (respects filters)."
              >
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{items.filter(i => i.status === 'pending').length}</p>
                  <p className="text-sm text-gray-500">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{items.filter(i => i.priority === 'high' && i.status === 'pending').length}</p>
                  <p className="text-sm text-gray-500">High Priority</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{items.filter(i => i.status === 'resolved').length}</p>
                  <p className="text-sm text-gray-500">Resolved</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{items.reduce((sum, i) => sum + (i.usn_count || 0), 0)}</p>
                  <p className="text-sm text-gray-500">Total Pieces</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Investigation Items */}
        <Card>
          <CardHeader>
            <CardTitle>Investigation Items</CardTitle>
            <CardDescription>
              {filteredItems.length} item(s) found
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : filteredItems.length === 0 ? (
              <div className="text-center py-12">
                <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                <p className="text-gray-500">No items in queue</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredItems.map((item) => (
                  <div 
                    key={item.investigation_id} 
                    className={`border rounded-lg p-4 ${item.priority === 'high' && item.status === 'pending' ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}
                    data-testid={`investigation-item-${item.investigation_id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold text-gray-900">{item.bundle_code}</span>
                          {item.type === 'combo_component_unavailable' && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700">Combo Pick</span>
                          )}
                          {getReasonBadge(item.reason)}
                          {getPriorityBadge(item.priority)}
                          {getStatusBadge(item.status)}
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <FileText className="w-4 h-4" />
                            Slip: {item.slip_no}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            Bin: {item.bin_location || 'N/A'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Package className="w-4 h-4" />
                            {item.usn_count} pcs
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          <span className="font-medium">{item.design_name || 'Unknown Design'}</span>
                          {item.sku_code && <span className="ml-2 text-gray-400">({item.sku_code})</span>}
                        </div>
                        <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            Reported by: {item.reported_by_name}
                          </span>
                          <span>
                            {formatDate(item.reported_at)}
                          </span>
                        </div>
                        {item.notes && (
                          <div className="text-sm text-gray-600 bg-gray-100 rounded p-2 mt-2">
                            <span className="font-medium">Notes:</span> {item.notes}
                          </div>
                        )}
                        {item.status === 'resolved' && (
                          <div className="text-sm text-green-700 bg-green-50 rounded p-2 mt-2">
                            <span className="font-medium">Resolution:</span> {item.resolution}
                            {item.action_taken && <span className="ml-2">- {item.action_taken}</span>}
                            <div className="text-xs text-green-600 mt-1">
                              Resolved by {item.resolved_by_name} on {formatDate(item.resolved_at)}
                            </div>
                          </div>
                        )}
                      </div>
                      {item.status === 'pending' && (
                        <Button
                          onClick={() => {
                            setSelectedItem(item);
                            setShowResolveDialog(true);
                          }}
                          className="bg-teal-600 hover:bg-teal-700"
                          data-testid={`resolve-btn-${item.investigation_id}`}
                        >
                          Resolve
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Resolve Dialog */}
      <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve Investigation</DialogTitle>
            <DialogDescription>
              Bundle: <span className="font-mono font-bold">{selectedItem?.bundle_code}</span>
              <br />
              Reason: {selectedItem?.reason}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Resolution *</label>
              <Select value={resolution} onValueChange={setResolution}>
                <SelectTrigger data-testid="resolution-select">
                  <SelectValue placeholder="Select resolution" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="found">Found - Bundle was located</SelectItem>
                  <SelectItem value="relocated">Relocated - Found in different location</SelectItem>
                  <SelectItem value="written_off">Written Off - Confirmed missing</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Action Taken / Notes</label>
              <Textarea
                placeholder="Describe the action taken..."
                value={actionTaken}
                onChange={(e) => setActionTaken(e.target.value)}
                rows={3}
                data-testid="action-taken-input"
              />
            </div>
            {resolution === 'written_off' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 inline mr-2" />
                This will mark the bundle as WRITTEN_OFF in inventory. This action cannot be undone.
              </div>
            )}
            {(resolution === 'found' || resolution === 'relocated') && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
                <CheckCircle className="w-4 h-4 inline mr-2" />
                This will restore the bundle to STORED status — it will be available for pick slips again.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleResolve} 
              disabled={resolving || !resolution}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="confirm-resolve-btn"
            >
              {resolving ? 'Resolving...' : 'Resolve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Mark Missing Dialog */}
      <Dialog open={showMarkMissingDialog} onOpenChange={setShowMarkMissingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Bundle as Missing</DialogTitle>
            <DialogDescription>
              Enter the bundle code to flag it as physically missing from SFG storage.
              The bundle will be removed from pick slip eligibility until resolved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Bundle Code *</label>
              <Input
                placeholder="e.g. BUN048588/25-26"
                value={missingBundleCode}
                onChange={(e) => setMissingBundleCode(e.target.value)}
                data-testid="missing-bundle-code-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Reason</label>
              <Select value={missingReason} onValueChange={setMissingReason}>
                <SelectTrigger data-testid="missing-reason-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_found">Not Found in Location</SelectItem>
                  <SelectItem value="damaged">Damaged</SelectItem>
                  <SelectItem value="misplaced">Misplaced</SelectItem>
                  <SelectItem value="audit_discrepancy">Audit Discrepancy</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                placeholder="Additional details about the missing bundle..."
                value={missingNotes}
                onChange={(e) => setMissingNotes(e.target.value)}
                rows={3}
                data-testid="missing-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMarkMissingDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleMarkMissing} 
              disabled={markingMissing || !missingBundleCode.trim()}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-mark-missing-btn"
            >
              {markingMissing ? 'Marking...' : 'Confirm Missing'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SFGInvestigationQueue;
